// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Photo from "./photo.js"
import PhotoType from "./photo-type.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "photo_id": {
      "column": {
        "default": null,
        "name": "photo_id",
        "null": false,
        "type": "uuid"
      },
      "name": "photo_id",
      "selected_by_default": null,
      "translated": null
    },
    "photo_type_id": {
      "column": {
        "default": null,
        "name": "photo_type_id",
        "null": false,
        "type": "uuid"
      },
      "name": "photo_type_id",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "PhotoPhotoType",
  "collectionKey": "photo_photo_types",
  "collectionName": "photo_photo_types",
  "i18nKey": "photo_photo_type",
  "camelizedLower": "photoPhotoType",
  "name": "PhotoPhotoType",
  "nameDasherized": "photo-photo-type",
  "pluralName": "photo_photo_types",
  "ransackable_associations": [
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "Photo",
      "collectionName": "photos",
      "foreignKey": "photo_id",
      "name": "photo",
      "macro": "belongs_to",
      "resource_name": "Photo",
      "through": null
    },
    {
      "className": "PhotoType",
      "collectionName": "photo_types",
      "foreignKey": "photo_type_id",
      "name": "photo_type",
      "macro": "belongs_to",
      "resource_name": "PhotoType",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "photo_id",
      "column": {
        "default": null,
        "name": "photo_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "photo_type_id",
      "column": {
        "default": null,
        "name": "photo_type_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "Photo",
      "collectionName": "photos",
      "foreignKey": "photo_id",
      "name": "photo",
      "macro": "belongs_to",
      "resource_name": "Photo",
      "through": null
    },
    {
      "className": "PhotoType",
      "collectionName": "photo_types",
      "foreignKey": "photo_type_id",
      "name": "photo_type",
      "macro": "belongs_to",
      "resource_name": "PhotoType",
      "through": null
    }
  ],
  "paramKey": "photo_photo_type",
  "primaryKey": "id"
}

/** Frontend model for PhotoPhotoType. */
export default class PhotoPhotoType extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string} */
  photoId() {
    return this.readAttributeUnderscore("photo_id")
  }

  /** @returns {boolean} */
  hasPhotoId() {
    const value = this.photoId()

    return this._isPresent(value)
  }

  /** @returns {string} */
  photoTypeId() {
    return this.readAttributeUnderscore("photo_type_id")
  }

  /** @returns {boolean} */
  hasPhotoTypeId() {
    const value = this.photoTypeId()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {import("./photo.js").default | null} */
  photo() {
    return this._readBelongsToReflection({modelClass: Photo, reflectionName: "photo"})
  }

  /** @returns {Promise<import("./photo.js").default | null>} */
  loadPhoto() {
    if (!("photoId" in this)) throw new Error("Foreign key method wasn't defined: photoId")

    const id = this.photoId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "photo",
        model: this,
        modelClass: Photo
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./photo.js").default | null>} */
  ensurePhotoLoaded() {
    return this.ensureAssociationLoaded("photo")
  }

  /** @returns {import("./photo-type.js").default | null} */
  photoType() {
    return this._readBelongsToReflection({modelClass: PhotoType, reflectionName: "photo_type"})
  }

  /** @returns {Promise<import("./photo-type.js").default | null>} */
  loadPhotoType() {
    if (!("photoTypeId" in this)) throw new Error("Foreign key method wasn't defined: photoTypeId")

    const id = this.photoTypeId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "photo_type",
        model: this,
        modelClass: PhotoType
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./photo-type.js").default | null>} */
  ensurePhotoTypeLoaded() {
    return this.ensureAssociationLoaded("photo_type")
  }
}
